import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {NoticeItem, PersonalPlanLandingBannerNoticeData} from '@udemy/notices';
import {Image} from '@udemy/react-core-components';
import {Ribbon} from '@udemy/react-messaging-components';
import {ConsumerSubscriptionPlan, SubscriptionPlanContext} from '@udemy/subscription-graphql';

import {PersonalPlanHeroBannerContent} from './personal-plan-hero-banner-content.react-component';
import {PersonalPlanHeroStats} from './personal-plan-hero-stats.react-component';
import styles from './personal-plan-hero.module.less';

/**
 * TODO: The art is expected to vary by price country, so this will likely become a
 * lookup keyed on country rather than a single constant.
 */
const DEFAULT_HERO_IMAGE_DESKTOP =
    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-hero/PersonalPlanRevampHeroDesktop.webp';
const DEFAULT_HERO_IMAGE_MOBILE =
    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-hero/PersonalPlanRevampHeroMobile.webp';

export interface PersonalPlanHeroProps {
    plan?: SubscriptionPlanContext<ConsumerSubscriptionPlan>;
    planLabelComponent?: React.ReactNode;
    notice?: NoticeItem<PersonalPlanLandingBannerNoticeData>;
    bannerFallbackTitle?: string;
    bannerFallbackSubtitle?: string;
}

export const PersonalPlanHero = (props: PersonalPlanHeroProps) => {
    const {gettext} = useI18n();
    const {
        plan,
        notice,
        bannerFallbackTitle = gettext('The best of Udemy, all in one plan'),
        bannerFallbackSubtitle = gettext(
            'Job skills evolve fast. So should you. Stay ahead with access to skills from real-world experts, hands-on practice exercises, and certification prep courses to advance your career.',
        ),
    } = props;

    const planLabelComponent = props.planLabelComponent ?? (
        <Ribbon color="gray-150" size="small" className={styles['plan-hero-ribbon']}>
            <Icon glyph="verified" size="xsmall" label={false} />
            {gettext('Join 550K+ on Personal Plan')}
        </Ribbon>
    );

    const heroArt = (
        <picture className={styles['plan-hero-image-wrapper']} data-testid="hero-image-placeholder">
            <source srcSet={DEFAULT_HERO_IMAGE_DESKTOP} media="(min-width: 701px)" />
            <Image className={styles['plan-hero-image']} src={DEFAULT_HERO_IMAGE_MOBILE} alt="" />
        </picture>
    );

    return (
        <div className={styles['plan-hero']}>
            <div className={styles['plan-hero-content']}>
                <div className={styles['plan-hero-content-banner-wrapper']}>
                    <div className={styles['plan-hero-content-banner']}>
                        <div className={styles['plan-hero-content-banner-inner']}>
                            <PersonalPlanHeroBannerContent
                                fallbackTitle={bannerFallbackTitle}
                                fallbackSubtitle={bannerFallbackSubtitle}
                                notice={notice}
                                label={gettext('Personal Plan')}
                                plan={plan}
                                planLabelComponent={planLabelComponent}
                            />
                        </div>
                    </div>
                </div>

                {/* The art bleeds to the frame edge, so it sits outside the container that
                    constrains the copy. */}
                {heroArt}
            </div>
            <div className={styles['plan-hero-stats']}>
                <PersonalPlanHeroStats />
            </div>
        </div>
    );
};
